import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Typography,
} from '@mui/material'
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Star as StarIcon, StarBorder as StarBorderIcon } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import integrationService from '../../services/integrationService'
import IntegrationConfigForm from '../integrations/IntegrationConfigForm'
import type { IntegrationConfig, IntegrationConfigCreate, IntegrationConfigUpdate } from '../../types/integration'
import { getErrorMessage } from '../../utils/errorHandlers'

const EMAIL_PROVIDERS = [
  { value: 'zeptomail', label: 'ZeptoMail' },
  { value: 'sendgrid', label: 'SendGrid' },
  { value: 'smtp', label: 'SMTP' },
]

const EmailProvidersTab: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [configs, setConfigs] = useState<IntegrationConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingConfig, setEditingConfig] = useState<IntegrationConfig | null>(null)
  const [newProvider, setNewProvider] = useState('')

  const loadConfigs = async () => {
    setLoading(true)
    try {
      const data = await integrationService.getIntegrationsByType('email', false)
      setConfigs(data.configs || [])
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to load email providers'), { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadConfigs()
  }, [])

  const openAddDialog = () => {
    setEditingConfig(null)
    setNewProvider('')
    setDialogOpen(true)
  }

  const openEditDialog = (config: IntegrationConfig) => {
    setEditingConfig(config)
    setNewProvider(config.provider_name)
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setEditingConfig(null)
    setNewProvider('')
  }

  const handleFormSubmit = async (data: IntegrationConfigCreate | IntegrationConfigUpdate) => {
    if (editingConfig) {
      await integrationService.updateIntegration(editingConfig.id, data as IntegrationConfigUpdate)
      enqueueSnackbar('Provider updated', { variant: 'success' })
    } else {
      await integrationService.createIntegration(data as IntegrationConfigCreate)
      enqueueSnackbar('Provider added', { variant: 'success' })
    }
    closeDialog()
    loadConfigs()
  }

  const handleSetPrimary = async (config: IntegrationConfig) => {
    try {
      await integrationService.setAsPrimary(config.id)
      enqueueSnackbar(`${config.provider_name} set as primary`, { variant: 'success' })
      loadConfigs()
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to set primary provider'), { variant: 'error' })
    }
  }

  const handleDelete = async (config: IntegrationConfig) => {
    if (!window.confirm(`Remove ${config.provider_name} provider?`)) return
    try {
      await integrationService.deleteIntegration(config.id)
      enqueueSnackbar('Provider removed', { variant: 'success' })
      loadConfigs()
    } catch (err: any) {
      enqueueSnackbar(getErrorMessage(err, 'Failed to remove provider'), { variant: 'error' })
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="body2" color="text.secondary">
          Configure one or more email providers. The primary provider is tried first; others act as failover, in
          priority order.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
          Add Provider
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Provider</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Primary</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No email providers configured yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              configs.map((config) => (
                <TableRow key={config.id} hover>
                  <TableCell>
                    {EMAIL_PROVIDERS.find((p) => p.value === config.provider_name)?.label || config.provider_name}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={config.is_active ? 'Active' : 'Inactive'}
                      color={config.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {config.is_primary ? (
                      <Chip size="small" icon={<StarIcon />} label="Primary" color="primary" />
                    ) : (
                      <Tooltip title="Set as primary">
                        <IconButton size="small" onClick={() => handleSetPrimary(config)}>
                          <StarBorderIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{config.priority}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditDialog(config)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => handleDelete(config)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingConfig ? `Edit ${editingConfig.provider_name}` : 'Add Email Provider'}</DialogTitle>
        <DialogContent>
          {!editingConfig && (
            <TextField
              select
              label="Provider"
              value={newProvider}
              onChange={(e) => setNewProvider(e.target.value)}
              fullWidth
              required
              sx={{ mt: 1, mb: 2 }}
            >
              <MenuItem value="">Select a provider</MenuItem>
              {EMAIL_PROVIDERS.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {(editingConfig || newProvider) && (
            <IntegrationConfigForm
              serviceType="email"
              providerName={editingConfig ? editingConfig.provider_name : newProvider}
              initialData={
                editingConfig
                  ? {
                    is_active: editingConfig.is_active,
                    is_primary: editingConfig.is_primary,
                    priority: editingConfig.priority,
                    config: editingConfig.config || editingConfig.config_data || {},
                  }
                  : undefined
              }
              integrationId={editingConfig?.id}
              onSubmit={handleFormSubmit}
              onCancel={closeDialog}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default EmailProvidersTab
